/**
 * @krisspy-file
 * @type page
 * @name "WhatsappMessages"
 * @title "WhatsApp Messages"
 * @description "Configure WhatsApp message templates"
 * @routes ["/whatsapp-messages"]
 * @flowName "communication"
 * @layout "DashboardLayout"
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save, Loader2, Plus, Pencil, Trash2, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { getLocations } from '../services/locations.service';
import NumberInput from '../components/NumberInput';
import type { Database } from '../lib/database.types';
import {
  getWhatsappMessages,
  createWhatsappMessage,
  updateWhatsappMessage,
  deleteWhatsappMessage,
  WhatsappMessage,
  WhatsappMessageInsert
} from '../services/whatsappMessages.service';

type Location = Database['public']['Tables']['locations']['Row'];

const MAX_MESSAGES = 10;

const CATEGORY_KEYS = [
  { value: 'initial', labelKey: 'whatsappMessages.categoryBookingConfirmation' },
  { value: 'codes', labelKey: 'whatsappMessages.categoryAccessCodes' },
  { value: 'scheduled', labelKey: 'whatsappMessages.categoryScheduledMessage' }
] as const;

const REFERENCE_OPTIONS = [
  { value: 'booking_time', label: 'Time of booking' },
  { value: 'rental_start', label: 'Rental start time' },
  { value: 'rental_end', label: 'Rental end time' }
] as const;

type TimeUnit = 'minutes' | 'hours' | 'days';

function minutesToUnit(totalMinutes: number): { value: number; unit: TimeUnit } {
  const abs = Math.abs(totalMinutes);
  if (abs === 0) return { value: 0, unit: 'minutes' };
  if (abs % 1440 === 0) return { value: abs / 1440, unit: 'days' };
  if (abs % 60 === 0) return { value: abs / 60, unit: 'hours' };
  return { value: abs, unit: 'minutes' };
}

function unitToMinutes(value: number, unit: TimeUnit): number {
  switch (unit) {
    case 'days': return value * 1440;
    case 'hours': return value * 60;
    default: return value;
  }
}

function formatOffset(offsetMinutes: number, reference: string, t: (key: string) => string): string {
  const refKey = reference === 'booking_time' ? 'timeOfBooking' : reference === 'rental_start' ? 'rentalStartTime' : 'rentalEndTime';
  const refLabel = t(`whatsappMessages.${refKey}`).toLowerCase();
  if (offsetMinutes === 0) {
    return `${t('whatsappMessages.at')} ${refLabel}`;
  }
  const abs = Math.abs(offsetMinutes);
  const { value, unit } = minutesToUnit(abs);
  const direction = offsetMinutes < 0 ? t('whatsappMessages.before').toLowerCase() : t('whatsappMessages.after').toLowerCase();
  const unitLabel = value === 1 ? unit.slice(0, -1) : unit;
  return `${value} ${unitLabel} ${direction} ${refLabel}`;
}

export default function WhatsappMessages() {
  const { t } = useTranslation();
  const { merchantId, accessControl } = useAuth();
  const navigate = useNavigate();
  const { defaultLanguage, availableLanguages } = useLanguage();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [messages, setMessages] = useState<WhatsappMessage[]>([]);
  const [editingMessage, setEditingMessage] = useState<WhatsappMessage | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    label: '',
    category: 'scheduled' as string,
    reference: 'booking_time' as string,
    direction: 'after' as 'before' | 'after',
    timeValue: 0,
    timeUnit: 'minutes' as TimeUnit,
    text_whatsapp: '',
    incl_codes: false,
    name_rental_items: ''
  });

  useEffect(() => {
    loadLocations();
  }, [merchantId]);

  useEffect(() => {
    if (selectedLocationId) {
      loadMessages();
    }
  }, [selectedLocationId, selectedLanguage]);

  async function loadLocations() {
    if (!merchantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await getLocations(accessControl);

    if (error) {
      console.error('Error loading locations:', error);
    } else if (data) {
      setLocations(data);
      if (data.length > 0) {
        setSelectedLocationId(data[0].id);
      }
    }
    setLoading(false);
  }

  async function loadMessages() {
    if (!selectedLocationId) return;

    setLoading(true);
    const { data, error } = await getWhatsappMessages(selectedLocationId, selectedLanguage);

    if (error) {
      console.error('Error loading WhatsApp messages:', error);
    }

    setMessages(data || []);
    setLoading(false);
  }

  function resetForm() {
    setFormData({
      label: '',
      category: 'scheduled',
      reference: 'booking_time',
      direction: 'after',
      timeValue: 0,
      timeUnit: 'minutes',
      text_whatsapp: '',
      incl_codes: false,
      name_rental_items: ''
    });
  }

  function startCreate() {
    setEditingMessage(null);
    resetForm();
    setIsCreating(true);
  }

  function startEdit(msg: WhatsappMessage) {
    const { value, unit } = minutesToUnit(msg.offset_minutes);
    setFormData({
      label: msg.label || '',
      category: msg.category || 'scheduled',
      reference: msg.reference,
      direction: msg.offset_minutes < 0 ? 'before' : 'after',
      timeValue: value,
      timeUnit: unit,
      text_whatsapp: msg.text_whatsapp || '',
      incl_codes: msg.incl_codes || false,
      name_rental_items: msg.name_rental_items || ''
    });
    setEditingMessage(msg);
    setIsCreating(true);
  }

  function cancelEdit() {
    setIsCreating(false);
    setEditingMessage(null);
    resetForm();
  }

  function computeOffsetMinutes(): number {
    const raw = unitToMinutes(formData.timeValue, formData.timeUnit);
    if (raw === 0) return 0;
    return formData.direction === 'before' ? -raw : raw;
  }

  async function handleSave() {
    if (!merchantId || !selectedLocationId) return;

    if (!formData.label.trim()) {
      showToast(t('whatsappMessages.labelRequired'), 'error');
      return;
    }

    const isInstant = formData.category === 'initial' || formData.category === 'codes';
    const offsetMinutes = isInstant ? 0 : computeOffsetMinutes();
    const reference = isInstant ? 'booking_time' : formData.reference;

    if (formData.category === 'scheduled' && reference === 'booking_time' && offsetMinutes === 0) {
      showToast(t('whatsappMessages.scheduledOffsetError'), 'error');
      return;
    }

    setSaving(true);

    const dataToSave: WhatsappMessageInsert = {
      merchant_id: merchantId,
      location_id: selectedLocationId,
      language: selectedLanguage,
      reference,
      offset_minutes: offsetMinutes,
      label: formData.label.trim(),
      category: formData.category,
      text_whatsapp: formData.text_whatsapp,
      incl_codes: formData.incl_codes,
      name_rental_items: formData.name_rental_items || null
    };

    let result;
    if (editingMessage?.id) {
      result = await updateWhatsappMessage(editingMessage.id, dataToSave);
    } else {
      result = await createWhatsappMessage(dataToSave);
    }

    if (result.error) {
      showToast(t('whatsappMessages.errorSaving') + result.error, 'error');
    } else {
      showToast(t('whatsappMessages.savedSuccess'), 'success');
      cancelEdit();
      await loadMessages();
    }

    setSaving(false);
  }

  async function handleDelete(id: string) {
    if (!confirm(t('whatsappMessages.confirmDelete'))) return;

    setDeleting(id);
    const { error } = await deleteWhatsappMessage(id);

    if (error) {
      showToast(t('whatsappMessages.errorDeleting') + error, 'error');
    } else {
      showToast(t('whatsappMessages.deletedSuccess'), 'success');
      await loadMessages();
    }
    setDeleting(null);
  }

  const isScheduled = formData.category === 'scheduled';
  const showDirectionSelector = isScheduled && formData.reference !== 'booking_time';

  if (loading && locations.length === 0) {
    return (
      <div className="container-centered">
        <Loader2 className="w-8 h-8 animate-spin text-blue" />
        <p className="mt-md text-secondary">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      <div className="flex items-center gap-md mb-xl">
        <button onClick={() => navigate('/dashboard')} className="btn btn-ghost p-xs">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="heading-page">{t('whatsappMessages.title')}</h1>
          <p className="text-sm text-secondary">{t('whatsappMessages.subtitle')}</p>
        </div>
      </div>

      <div className="space-y-lg">
        {/* Location Selector */}
        <div className="card mb-lg">
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">{t('whatsappMessages.selectLocation')}</label>
              <select
                value={selectedLocationId}
                onChange={(e) => { setSelectedLocationId(e.target.value); cancelEdit(); }}
                className="input w-full max-w-md mt-sm"
              >
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.location_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Language Selector */}
        {availableLanguages.length > 1 && (
          <div className="card mb-lg">
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">{t('whatsappMessages.messageLanguage')}</label>
                <p className="text-sm text-secondary mb-sm">
                  {t('whatsappMessages.languageDesc')}
                </p>
                <div className="flex gap-sm mt-sm overflow-x-auto">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => { setSelectedLanguage(lang.code); cancelEdit(); }}
                      className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                        selectedLanguage === lang.code
                          ? 'bg-blue text-white'
                          : 'bg-secondary text-secondary hover:bg-primary/10'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-sm font-medium">{lang.label}</span>
                      {lang.code === defaultLanguage && (
                        <span className="text-xs opacity-75 ml-xs">{t('common.default')}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messages List */}
        {selectedLocationId && !isCreating && (
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <div>
                <h2 className="heading-section">{t('whatsappMessages.configuredMessages')}</h2>
                <p className="text-sm text-secondary mt-xs">
                  {t('whatsappMessages.templatesCount', { count: messages.length, max: MAX_MESSAGES })}
                </p>
              </div>
              {messages.length < MAX_MESSAGES && (
                <button onClick={startCreate} className="btn btn-primary">
                  <Plus className="w-4 h-4" />
                  {t('whatsappMessages.addMessage')}
                </button>
              )}
            </div>
            <div className="card-body">
              {messages.length === 0 ? (
                <div className="text-center py-xl text-secondary">
                  <Clock className="w-10 h-10 mx-auto mb-md opacity-40" />
                  <p>{t('whatsappMessages.noMessages')}</p>
                  <p className="text-sm mt-xs">{t('whatsappMessages.noMessagesHint')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-primary/10">
                        <th className="text-left py-sm px-md text-xs font-medium text-secondary uppercase">{t('whatsappMessages.labelHeader')}</th>
                        <th className="text-left py-sm px-md text-xs font-medium text-secondary uppercase">{t('whatsappMessages.categoryHeader')}</th>
                        <th className="text-left py-sm px-md text-xs font-medium text-secondary uppercase">{t('whatsappMessages.timingHeader')}</th>
                        <th className="text-left py-sm px-md text-xs font-medium text-secondary uppercase">{t('whatsappMessages.codesHeader')}</th>
                        <th className="text-right py-sm px-md text-xs font-medium text-secondary uppercase">{t('whatsappMessages.actionsHeader')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((msg) => (
                        <tr key={msg.id} className="border-b border-primary/5 hover:bg-primary/3">
                          <td className="py-md px-md">
                            <span className="font-medium text-primary">{msg.label || t('whatsappMessages.noLabel')}</span>
                          </td>
                          <td className="py-md px-md">
                            <span className="text-xs px-sm py-xs rounded-full bg-blue-100 text-blue-800">
                              {(() => { const ck = CATEGORY_KEYS.find(c => c.value === msg.category); return ck ? t(ck.labelKey) : msg.category; })()}
                            </span>
                          </td>
                          <td className="py-md px-md">
                            <span className="text-sm text-secondary">
                              {msg.category === 'scheduled'
                                ? formatOffset(msg.offset_minutes, msg.reference, t)
                                : msg.category === 'codes' ? t('whatsappMessages.instantCutoffTiming') : t('whatsappMessages.instantTiming')}
                            </span>
                          </td>
                          <td className="py-md px-md">
                            <span className={`text-xs px-sm py-xs rounded-full ${msg.incl_codes ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                              {msg.incl_codes ? t('common.yes') : t('common.no')}
                            </span>
                          </td>
                          <td className="py-md px-md text-right">
                            <div className="flex items-center justify-end gap-xs">
                              <button
                                onClick={() => startEdit(msg)}
                                className="btn btn-ghost p-xs"
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(msg.id)}
                                disabled={deleting === msg.id}
                                className="btn btn-ghost p-xs text-danger hover:bg-danger/10"
                                title="Delete"
                              >
                                {deleting === msg.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Message Form (Create/Edit) */}
        {selectedLocationId && isCreating && (
          <div className="card">
            <div className="card-header">
              <h2 className="heading-section">
                {editingMessage ? t('whatsappMessages.editMessage') : t('whatsappMessages.newMessage')}
              </h2>
            </div>
            <div className="card-body space-y-lg">
              {/* Label */}
              <div className="form-group">
                <label className="form-label block">{t('whatsappMessages.label')} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="input w-full max-w-md mt-sm"
                  placeholder="e.g., Welcome message, Pickup reminder, Return codes"
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('whatsappMessages.labelHint')}
                </p>
              </div>

              {/* Category */}
              <div className="form-group">
                <label className="form-label block">{t('whatsappMessages.category')}</label>
                <select
                  value={formData.category}
                  onChange={(e) => {
                    const cat = e.target.value;
                    setFormData({
                      ...formData,
                      category: cat,
                      ...(cat !== 'scheduled' ? { reference: 'booking_time', timeValue: 0, direction: 'after' as const } : {})
                    });
                  }}
                  className="input w-full max-w-md mt-sm"
                >
                  {CATEGORY_KEYS.map(opt => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </select>
                <p className="text-xs text-secondary mt-xs">
                  {formData.category === 'initial' && t('whatsappMessages.categoryInitialDesc')}
                  {formData.category === 'codes' && t('whatsappMessages.categoryCodesDesc')}
                  {formData.category === 'scheduled' && t('whatsappMessages.categoryScheduledDesc')}
                </p>
              </div>

              {/* Reference (Send relative to) - only for scheduled */}
              {isScheduled && (
              <div className="form-group">
                <label className="form-label block">{t('whatsappMessages.sendRelativeTo')}</label>
                <select
                  value={formData.reference}
                  onChange={(e) => {
                    const ref = e.target.value;
                    setFormData({
                      ...formData,
                      reference: ref,
                      direction: ref === 'booking_time' ? 'after' : formData.direction
                    });
                  }}
                  className="input w-full max-w-md mt-sm"
                >
                  {REFERENCE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{t(`whatsappMessages.${opt.value === 'booking_time' ? 'timeOfBooking' : opt.value === 'rental_start' ? 'rentalStartTime' : 'rentalEndTime'}`)}</option>
                  ))}
                </select>
              </div>
              )}

              {/* Timing - only for scheduled */}
              {isScheduled && (
              <div className="form-group">
                <label className="form-label flex items-center gap-sm">
                  <Clock className="w-4 h-4" />
                  {t('whatsappMessages.timing')}
                </label>
                <div className="flex items-center gap-md flex-wrap">
                  <NumberInput
                    value={formData.timeValue}
                    onChange={(value) => setFormData({ ...formData, timeValue: value })}
                    min={0}
                    mode="int"
                    className="input w-24"
                  />
                  <select
                    value={formData.timeUnit}
                    onChange={(e) => setFormData({ ...formData, timeUnit: e.target.value as TimeUnit })}
                    className="input w-32"
                  >
                    <option value="minutes">{t('whatsappMessages.minutes')}</option>
                    <option value="hours">{t('whatsappMessages.hours')}</option>
                    <option value="days">{t('whatsappMessages.days')}</option>
                  </select>
                  {showDirectionSelector ? (
                    <div className="flex rounded-md overflow-hidden border border-primary/20">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, direction: 'before' })}
                        className={`px-md py-sm text-sm font-medium transition-colors ${
                          formData.direction === 'before'
                            ? 'bg-blue text-white'
                            : 'bg-white text-secondary hover:bg-primary/5'
                        }`}
                      >
                        {t('whatsappMessages.before')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, direction: 'after' })}
                        className={`px-md py-sm text-sm font-medium transition-colors ${
                          formData.direction === 'after'
                            ? 'bg-blue text-white'
                            : 'bg-white text-secondary hover:bg-primary/5'
                        }`}
                      >
                        {t('whatsappMessages.after')}
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-secondary">{t('whatsappMessages.afterBooking')}</span>
                  )}
                </div>
                <p className="text-xs text-secondary mt-xs">
                  {formData.timeValue === 0
                    ? `${t('whatsappMessages.messageSentAt')} ${t(`whatsappMessages.${formData.reference === 'booking_time' ? 'timeOfBooking' : formData.reference === 'rental_start' ? 'rentalStartTime' : 'rentalEndTime'}`).toLowerCase()}`
                    : `${t('whatsappMessages.messageSentAt')} ${formatOffset(computeOffsetMinutes(), formData.reference, t)}`
                  }
                </p>
              </div>
              )}

              {/* WhatsApp Text */}
              <div className="form-group">
                <label className="form-label block">{t('whatsappMessages.messageText')}</label>
                <textarea
                  value={formData.text_whatsapp}
                  onChange={(e) => setFormData({ ...formData, text_whatsapp: e.target.value })}
                  className="input w-full"
                  rows={8}
                  placeholder={t('whatsappMessages.textPlaceholder')}
                />
                <p className="text-xs text-secondary mt-xs">
                  Available placeholders: {'{{customer_name}}'}, {'{{order_id}}'}, {'{{start_date}}'}, {'{{location_name}}'}, {'{{products_list}}'}
                </p>
              </div>

              {/* Include Codes Checkbox */}
              <div className="form-group">
                <label className="flex items-center gap-sm">
                  <input
                    type="checkbox"
                    checked={formData.incl_codes}
                    onChange={(e) => setFormData({ ...formData, incl_codes: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('whatsappMessages.includeAccessCodes')}</span>
                </label>
              </div>

              {/* Name for Rental Items */}
              <div className="form-group">
                <label className="form-label block">{t('whatsappMessages.rentalItemsLabel')}</label>
                <input
                  type="text"
                  value={formData.name_rental_items}
                  onChange={(e) => setFormData({ ...formData, name_rental_items: e.target.value })}
                  className="input w-full max-w-md mt-sm"
                  placeholder="e.g., bikes, equipment, items"
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('whatsappMessages.rentalItemsHint')}
                </p>
              </div>

              {/* Save / Cancel Buttons */}
              <div className="mt-lg flex justify-end gap-sm">
                <button onClick={cancelEdit} className="btn btn-ghost">
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingMessage ? t('whatsappMessages.updateMessage') : t('whatsappMessages.saveMessage')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
